import React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';

import { Link } from 'ui/base/link';
import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { PlainButton } from 'ui/public/button';

import { SwitchInput } from 'ui/standard/input/switch_input';
import { PlainPolicyEntry } from 'ui/schedules/policy_entry/policy_entry';
import { createPolicyEntries, getPolicyEntryID } from 'ui/schedules/policy_entry/policy_entry_logic';
import { PlainDeviceEntry } from 'ui/schedules/device_entry/device_entry';
import { createDeviceEntries, getDeviceEntryID } from 'ui/schedules/device_entry/device_entry_logic';

import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain, Schedule } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { ORDERED_WEEKDAYS, getShortWeekday } from 'assets/enums/weekdays/weekdays';

import { Redirect } from 'pages/core/redirect/redirect';

import { ViewScheduleStore } from './view_schedule_store';

import Messages from './view_schedule_messages';
import baseStyles from './view_schedule.module.css';

type ViewScheduleStyles = {
  headerContainer: string;
  editLink: string;
  nameContainer: string;
  switchContainer: string;
  detailContainer: string;
  labelContainer: string;
  detailContent: string;
  deleteButton: string;
};

const themedStyles: ThemedStyles<ViewScheduleStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: baseStyles.headerContainer,
    editLink: baseStyles.daylightLink,
    nameContainer: baseStyles.daylightNameContainer,
    switchContainer: baseStyles.switchContainer,
    detailContainer: baseStyles.daylightDetailContainer,
    labelContainer: baseStyles.daylightLabelContainer,
    detailContent: baseStyles.detailContent,
    deleteButton: baseStyles.daylightDeleteButton,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: baseStyles.headerContainer,
    editLink: baseStyles.midnightLink,
    nameContainer: baseStyles.midnightNameContainer,
    switchContainer: baseStyles.switchContainer,
    detailContainer: baseStyles.midnightDetailContainer,
    labelContainer: baseStyles.midnightLabelContainer,
    detailContent: baseStyles.detailContent,
    deleteButton: baseStyles.midnightDeleteButton,
  },
};

type ScheduleDetailsProps = {
  schedule: Schedule;
};

const ScheduleDetails = observer(({ schedule }: ScheduleDetailsProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.detailContainer}>
      <div className={styles.labelContainer}>
        {Messages.schedule()}
      </div>
      <div className={styles.detailContent}>
        {schedule.start.toHumanString()} - {schedule.end.toHumanString()}
      </div>
      <div className={styles.detailContent}>
        {ORDERED_WEEKDAYS.filter((day) => schedule.days[day]).map(day => getShortWeekday(day)).join(', ')}
      </div>
    </div>
  );
});

type PoliciesDisplayProps = {
  schedule: Schedule;
};

const PoliciesDisplay = observer(({ schedule }: PoliciesDisplayProps) => {
  const styles = useStyles(themedStyles);
  const { entries } = createPolicyEntries(schedule);
  return (
    <div className={styles.detailContainer}>
      <div className={styles.labelContainer}>
        {Messages.blockedApps()}
      </div>
      {entries.map((entry) => (
        <PlainPolicyEntry entry={entry} key={getPolicyEntryID(entry)}/>
      ))}
    </div>
  );
});

type DevicesDisplayProps = {
  domain: IPangolinDomain;
  schedule: Schedule;
};

const DevicesDisplay = observer(({ domain, schedule }: DevicesDisplayProps) => {
  const styles = useStyles(themedStyles);
  const entries = createDeviceEntries(schedule, domain.hosts);
  return (
    <div className={styles.detailContainer}>
      <div className={styles.labelContainer}>
        {Messages.managedDevices()}
      </div>
      {entries.map((entry) => (
        <PlainDeviceEntry entry={entry} key={getDeviceEntryID(entry)}/>
      ))}
    </div>
  );
});


type BaseViewScheduleProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  schedule: Schedule;
};

export const BaseViewSchedule = observer(({ pangolin, domain, schedule }: BaseViewScheduleProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new ViewScheduleStore(schedule));
  React.useEffect(() => { setStore(new ViewScheduleStore(schedule)); }, [schedule]);

  const handleToggle = React.useCallback(async() => {
    const result = await store.handleToggle(domain);
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, [store, domain]);

  const handleDelete = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleDelete(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, domain, history]);

  return (
    <StandardPage>
      <form onSubmit={handleDelete}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <Link path={{ kind: BasePath.SCHEDULE_EDIT, pangolinID: domain.id, scheduleID: schedule.uuid }} className={styles.editLink}>
            {Messages.edit()}
          </Link>
        </div>
        <div className={styles.nameContainer}>
          <span>
            {schedule.name}
          </span>
          <div className={styles.switchContainer}>
            <SwitchInput store={store.enabled} onChange={handleToggle}/>
          </div>
        </div>
        <ScheduleDetails schedule={schedule}/>
        <PoliciesDisplay schedule={schedule}/>
        <DevicesDisplay domain={domain} schedule={schedule}/>
        <PlainButton type='submit'
          busy={store.busy} reserveBusyIconArea={false}
          disabled={!pangolin.onlineStatus}
          className={styles.deleteButton}>{Messages.deleteRule()}</PlainButton>
      </form>
    </StandardPage>
  );
});

export const ViewSchedule = observer(() => {
  const { pangolinID, scheduleID } = useParams<{ pangolinID: string, scheduleID: string }>();
  if (!pangolinID) return <Redirect />;
  if (!scheduleID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  const schedule = domain.schedules.get(scheduleID);
  if (!schedule) return <Redirect />;

  return (
    <BaseViewSchedule
      pangolin={pangolin}
      domain={domain}
      schedule={schedule}
    />
  );
});
